/**
 * VoiceSchedulerInput — input vocale riutilizzabile per pianificare task.
 *
 * iter v28 r391 — User M1300: «Component riutilizzabile in Assistente +
 * Sopralluogo + scheda annuncio». Permette all'agente di parlare/scrivere
 * cosa vuole pianificare in qualsiasi momento, senza dover andare alla
 * pagina Programmati.
 *
 * Layout compatto inline:
 *   [textarea con placeholder] [🎙 Parla] [Pianifica]
 *
 * Quando l'utente preme Pianifica/Parla:
 *  1. Manda /api/scheduled-tasks/from-voice
 *  2. Backend usa Claude per estrarre kind/trigger_at/payload
 *  3. Toast con conferma + (opzionale) onSuccess callback
 *
 * Props:
 *  - context: { property_id?, contact_id?, ... } passato al backend per
 *             arricchire il parsing (es. "manda foto al venditore" sa già
 *             di che annuncio sta parlando)
 *  - placeholder: testo placeholder personalizzato
 *  - compact: layout più piccolo (es. per inline in annuncio)
 *  - onSuccess: callback dopo creazione task (es. refresh lista o
 *               chiusura modal)
 *  - className: classi extra wrapper
 */
import { useState } from "react";
import { toast } from "sonner";
import { Microphone, Check, Spinner, CalendarCheck } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";

const DEFAULT_PLACEHOLDER =
  'Es: "manda gli auguri di Natale il 25 dicembre alle 9" · "manda questo annuncio ai clienti interessati domani" · "tra 6 mesi manda foto e grazie al signor Ferrari"';

export default function VoiceSchedulerInput({
  context,
  placeholder = DEFAULT_PLACEHOLDER,
  compact = false,
  onSuccess,
  className = "",
  testIdPrefix = "voice-scheduler",
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  const submit = async (textOverride) => {
    const finalText = (textOverride || text).trim();
    if (finalText.length < 5) {
      toast.error("Scrivi o dimmi qualcosa di più chiaro");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { text: finalText };
      if (context) payload.context = context;
      const { data } = await api.post("/scheduled-tasks/from-voice", payload);
      if (data?.error) {
        toast.error(data.error);
      } else if (data?.task) {
        const triggerDate = new Date(data.task.trigger_at);
        const dateLabel = triggerDate.toLocaleDateString("it-IT", {
          day: "numeric", month: "long",
        });
        const timeLabel = triggerDate.toLocaleTimeString("it-IT", {
          hour: "2-digit", minute: "2-digit",
        });
        toast.success(`Pianificato per ${dateLabel} alle ${timeLabel}`, {
          description: "Lo trovi in Programmati. Ti avvisiamo al momento giusto.",
        });
        setText("");
        if (onSuccess) onSuccess(data.task);
      }
    } catch (e) {
      toast.error(apiError(e, "Pianificazione fallita"));
    } finally {
      setSubmitting(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        setSubmitting(true);
        try {
          const { data } = await api.post("/voice/transcribe-only", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const transcript = data?.transcript || data?.text || "";
          if (transcript) {
            setText(transcript);
            await submit(transcript);
          } else {
            toast.error("Non ho capito. Riprova parlando chiaro.");
          }
        } catch (e) {
          toast.error(apiError(e, "Trascrizione fallita"));
        } finally {
          setSubmitting(false);
        }
      };
      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    setRecording(false);
  };

  const cls = compact
    ? "bg-white border border-[#E5E7E0] rounded-lg p-3"
    : "bg-white border border-[#E5E7E0] rounded-2xl p-5 shadow-sm";

  return (
    <div className={`${cls} ${className}`} data-testid={`${testIdPrefix}-wrapper`}>
      <div className="flex items-center gap-1.5 mb-2">
        <CalendarCheck size={13} weight="fill" className="text-[#7C8B6E]" />
        <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
          Pianifica con la voce
        </span>
      </div>
      <div className={`flex ${compact ? "flex-row" : "flex-col sm:flex-row"} gap-2`}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          rows={compact ? 2 : 3}
          className="flex-1 text-[13.5px] text-[#1C1C1A] bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg px-3 py-2 focus:outline-none focus:border-[#7C8B6E] resize-none leading-snug"
          data-testid={`${testIdPrefix}-textarea`}
          disabled={submitting || recording}
        />
        <div className={`flex ${compact ? "flex-col" : "sm:flex-col"} gap-1.5`}>
          {!recording ? (
            <button
              type="button"
              onClick={startRecording}
              disabled={submitting}
              className="px-3 py-2 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              data-testid={`${testIdPrefix}-mic-btn`}
              aria-label="Registra"
            >
              <Microphone size={13} weight="fill" />
              {compact ? "" : "Parla"}
            </button>
          ) : (
            <button
              type="button"
              onClick={stopRecording}
              className="px-3 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 animate-pulse"
              data-testid={`${testIdPrefix}-stop-btn`}
            >
              <Spinner size={13} className="animate-spin" />
              {compact ? "" : "Fermo"}
            </button>
          )}
          <button
            type="button"
            onClick={() => submit()}
            disabled={submitting || !text.trim() || recording}
            className="px-3 py-2 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-submit-btn`}
          >
            {submitting ? <Spinner size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
            {compact ? "" : "Pianifica"}
          </button>
        </div>
      </div>
    </div>
  );
}
